import { useCallback, useEffect, useRef, useState } from 'react';
import { getMessageThreads } from '../services/collaborationService';

const POLL_INTERVAL_MS = 10000;

export default function useUnreadMessageCount({ enabled = true, onNewMessages } = {}) {
  const [count, setCount] = useState(0);
  const [threads, setThreads] = useState([]);

  // Latest callback held in a ref so fetchThreads can stay stable
  // (otherwise the interval would be torn down on every parent render).
  const onNewMessagesRef = useRef(onNewMessages);
  useEffect(() => {
    onNewMessagesRef.current = onNewMessages;
  }, [onNewMessages]);

  const prevThreadCountsRef = useRef(new Map());
  const isFirstRunRef = useRef(true);

  const fetchThreads = useCallback(async () => {
    try {
      const allThreads = await getMessageThreads();

      // Detect new-message deltas after the first run.
      if (!isFirstRunRef.current) {
        const events = [];
        allThreads.forEach((thread) => {
          const key = thread.threadKey || thread.listing?.id || thread.listing?._id;
          if (!key) return;
          const prev = prevThreadCountsRef.current.get(key) ?? 0;
          if (thread.unreadCount > prev) {
            events.push({
              threadKey: key,
              listingId: thread.listing?.id || thread.listing?._id,
              title: thread.listing?.title || thread.listing?.humanId || '',
              text: thread.lastMessage?.text || '',
              delta: thread.unreadCount - prev,
            });
          }
        });
        if (events.length > 0) {
          onNewMessagesRef.current?.(events);
        }
      }

      // Update the ref map with the fresh counts and reset first-run flag.
      const nextCounts = new Map();
      allThreads.forEach((thread) => {
        const key = thread.threadKey || thread.listing?.id || thread.listing?._id;
        if (key) {
          nextCounts.set(key, thread.unreadCount);
        }
      });
      prevThreadCountsRef.current = nextCounts;
      isFirstRunRef.current = false;

      const unreadThreads = allThreads
        .filter((t) => t.unreadCount > 0)
        .sort(
          (a, b) =>
            new Date(b.updatedAt || 0).getTime() -
            new Date(a.updatedAt || 0).getTime(),
        );
      setThreads(unreadThreads);
      setCount(allThreads.reduce((sum, t) => sum + (t.unreadCount || 0), 0));
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    fetchThreads();
    const interval = setInterval(fetchThreads, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled, fetchThreads]);

  return { count, threads };
}
